// Диагностика голоса TESTCS — экран LIVE VOICE HEALTH & CONSENTED AUTO-GAIN.
//
// Главное правило экрана, ради которого он и существует: МЫ НИКОГДА НЕ МЕНЯЕМ
// ГОЛОС САМИ. Модуль только измеряет и предлагает. Любое изменение проходит
// через явное согласие игрока (режим 'ask'), либо он заранее включил
// автоматику (режим 'auto', opt-in), либо просит только предупреждать
// ('warn'). Поэтому applySuggestion здесь нет — есть suggestionFor(), а
// применяет экран, по кнопке.
//
// Метрики считаются из того же анализатора, что и в voice-chain.js:
// RMS, пик, шумовой порог, счётчик клиппинга и история пиков за 60 секунд.

const CONSENT_KEY = 'testcs.voice.consent.v1'
const DEVICE_KEY = 'testcs.voice.devices.v1'

// ─── Границы зон (dBFS по RMS) — те же, что рисует шкала на экране ──────────

export const ZONES = {
  quietMax: -42, // ниже — «слишком тихо»
  goodMax: -12, // рабочий диапазон: -42…-12
  loudMax: -6, // -12…-6 — «слишком громко»
  // выше -6 по RMS или пик к нулю — клиппинг
  clipPeakDb: -0.5,
  targetRmsDb: -20, // куда целимся, когда предлагаем усиление
}

export const VERDICTS = {
  quiet: { id: 'quiet', label: 'TOO QUIET', ru: 'СЛИШКОМ ТИХО', tone: 'warning', peerHint: 'We can barely hear you' },
  good: { id: 'good', label: 'GOOD', ru: 'ХОРОШО', tone: 'success', peerHint: 'Your audio sounds great' },
  loud: { id: 'loud', label: 'TOO LOUD', ru: 'СЛИШКОМ ГРОМКО', tone: 'warning', peerHint: 'Consider lowering your volume' },
  clipping: { id: 'clipping', label: 'CLIPPING', ru: 'ПЕРЕГРУЗ', tone: 'error', peerHint: 'Audio is clipping' },
  silent: { id: 'silent', label: 'NO SIGNAL', ru: 'НЕТ СИГНАЛА', tone: 'muted', peerHint: 'Microphone is silent' },
}

export function verdictFor({ rmsDb, peakDb }) {
  if (!Number.isFinite(rmsDb) || rmsDb <= -85) return VERDICTS.silent
  if (peakDb >= ZONES.clipPeakDb || rmsDb > ZONES.loudMax) return VERDICTS.clipping
  if (rmsDb > ZONES.goodMax) return VERDICTS.loud
  if (rmsDb < ZONES.quietMax) return VERDICTS.quiet
  return VERDICTS.good
}

// Положение значения на шкале -60…0 → 0..1
export function dbToScale(db) {
  const v = Math.max(-60, Math.min(0, Number.isFinite(db) ? db : -60))
  return (v + 60) / 60
}

// ─── Монитор: экран тикает его каждый кадр, монитор копит статистику ────────

export class VoiceHealthMonitor {
  constructor({ historySeconds = 60, points = 120 } = {}) {
    this.historySeconds = historySeconds
    this.points = points
    this.reset()
  }

  reset() {
    this.rmsDb = -100
    this.peakDb = -100
    this.peakHoldDb = -100
    this.peakHoldAt = 0
    this.peakHoldAge = 0
    this.clippingCount = 0
    this.noiseFloorDb = -100
    this.history = [] // { t, rmsDb, peakDb }
    this._floorSamples = []
    this._lastPoint = 0
    this._lastClipAt = 0
    this._startedAt = 0
  }

  // levels — результат VoiceChain.levels(); now — performance.now()
  tick(levels, now) {
    if (!levels) return this.snapshot()
    if (!this._startedAt) this._startedAt = now
    const { rmsDb, peakDb } = levels
    this.rmsDb = rmsDb
    this.peakDb = peakDb

    // Peak hold: держим максимум, отпускаем через 2 секунды
    if (peakDb >= this.peakHoldDb || now - this.peakHoldAt > 2000) {
      this.peakHoldDb = peakDb
      this.peakHoldAt = now
    }
    this.peakHoldAge = (now - this.peakHoldAt) / 1000

    // Клиппинг: считаем событием, а не каждым кадром — иначе счётчик врёт
    if (peakDb >= ZONES.clipPeakDb && now - this._lastClipAt > 250) {
      this.clippingCount++
      this._lastClipAt = now
    }

    // Шумовой порог: 10-й перцентиль RMS за последние ~12 секунд.
    // Берём именно перцентиль, а не минимум — минимум ловит случайные провалы.
    if (Number.isFinite(rmsDb) && rmsDb > -100) {
      this._floorSamples.push({ t: now, db: rmsDb })
      const cutoff = now - 12000
      while (this._floorSamples.length && this._floorSamples[0].t < cutoff) this._floorSamples.shift()
      if (this._floorSamples.length > 20) {
        const sorted = this._floorSamples.map(s => s.db).sort((a, b) => a - b)
        this.noiseFloorDb = sorted[Math.floor(sorted.length * 0.1)]
      }
    }

    // История пиков: точка раз в (60с / points) ≈ 500 мс
    const step = (this.historySeconds * 1000) / this.points
    if (now - this._lastPoint >= step) {
      this._lastPoint = now
      this.history.push({ t: now, rmsDb, peakDb })
      const cutoff = now - this.historySeconds * 1000
      while (this.history.length && this.history[0].t < cutoff) this.history.shift()
    }

    return this.snapshot()
  }

  get verdict() {
    return verdictFor({ rmsDb: this.rmsDb, peakDb: this.peakDb })
  }

  // Достаточно ли накоплено, чтобы предлагать изменения
  get settled() {
    return this._floorSamples.length > 40
  }

  snapshot() {
    return {
      rmsDb: this.rmsDb,
      peakDb: this.peakDb,
      peakHoldDb: this.peakHoldDb,
      peakHoldAge: this.peakHoldAge,
      clippingCount: this.clippingCount,
      noiseFloorDb: this.noiseFloorDb,
      history: this.history,
      verdict: this.verdict,
      settled: this.settled,
    }
  }
}

// ─── Предложение: что именно мы предлагаем поправить и почему ──────────────
//
// Возвращает null, когда всё в порядке. Никогда ничего не применяет.

export function suggestionFor(snapshot, settings) {
  if (!snapshot || !snapshot.settled) return null
  const verdict = snapshot.verdict
  if (verdict.id === 'silent' || verdict.id === 'good') return null

  const currentGain = Number(settings?.outputGain) || 0

  if (verdict.id === 'quiet') {
    // сколько не хватает до цели, округляем до целого dB
    const delta = Math.round(Math.min(12, Math.max(1, ZONES.targetRmsDb - snapshot.rmsDb)))
    return {
      id: 'too-quiet',
      kind: 'quiet',
      title: 'We can barely hear you',
      ru: 'Тебя почти не слышно',
      body: 'Increasing your gain can help your teammates hear you better.',
      bodyRu: 'Прибавка усиления поможет команде тебя расслышать.',
      changes: [{ id: 'outputGain', label: 'Input Gain', value: currentGain + delta, display: `${delta > 0 ? '+' : ''}${delta} dB` }],
      why: ['Голос попадает в рабочий диапазон', 'Не нужно кричать в микрофон', 'Меньше шансов, что тебя переспросят'],
    }
  }

  // громко или перегруз
  const over = Math.max(snapshot.rmsDb - ZONES.targetRmsDb, snapshot.peakDb - -3)
  const delta = -Math.round(Math.min(12, Math.max(1, over)))
  const changes = [{ id: 'outputGain', label: 'Input Gain', value: currentGain + delta, display: `${delta} dB` }]
  if (!Number(settings?.limiter)) {
    changes.push({ id: 'limiter', label: 'Enable Limiter', value: 2, display: 'On' })
  }
  return {
    id: verdict.id === 'clipping' ? 'clipping' : 'too-loud',
    kind: verdict.id,
    title: verdict.id === 'clipping' ? 'Your voice is clipping' : 'Your voice is louder than ideal',
    ru: verdict.id === 'clipping' ? 'Сигнал перегружен' : 'Голос громче, чем нужно',
    body: 'Lowering gain and enabling the limiter can prevent distortion and clipping.',
    bodyRu: 'Убавить усиление и включить лимитер — уйдут искажения и перегруз.',
    changes,
    why: ['Убирает искажения и перегруз', 'Голос остаётся естественным', 'Комфортнее для ушей команды'],
  }
}

// Применить изменения предложения к настройкам — чистая функция.
// Вызывает её только экран, по явной кнопке игрока.
export function applyChanges(settings, changes) {
  const next = { ...settings }
  for (const change of changes || []) next[change.id] = change.value
  return next
}

// ─── Согласие и память по устройствам ───────────────────────────────────────

export const CONSENT_MODES = [
  {
    id: 'ask',
    label: 'ASK BEFORE CHANGES',
    ru: 'СПРАШИВАТЬ ПЕРЕД ИЗМЕНЕНИЯМИ',
    hint: "We'll suggest changes and ask your permission.",
    hintRu: 'Предложим изменение и спросим разрешения.',
    recommended: true,
  },
  {
    id: 'warn',
    label: 'ONLY WARN ME',
    ru: 'ТОЛЬКО ПРЕДУПРЕЖДАТЬ',
    hint: "We'll warn you if we detect issues, but won't suggest changes.",
    hintRu: 'Предупредим о проблеме, но менять ничего не предложим.',
  },
  {
    id: 'auto',
    label: 'AUTOMATIC GAIN (OPT-IN)',
    ru: 'АВТОМАТИЧЕСКОЕ УСИЛЕНИЕ (ПО СОГЛАСИЮ)',
    hint: 'Automatically adjust gain to keep you in the target range.',
    hintRu: 'Сами держим усиление в рабочем диапазоне. Изменения обратимы.',
    warn: 'Changes are still reversible.',
  },
]

export const DEFAULT_CONSENT = {
  mode: 'ask',
  testLocallyBeforeApplying: true,
  perDeviceMemory: true,
  dismissed: {}, // id предложения → true («не спрашивать снова»)
}

export function loadConsent() {
  try {
    const raw = JSON.parse(localStorage.getItem(CONSENT_KEY) || 'null')
    if (!raw) return { ...DEFAULT_CONSENT, dismissed: {} }
    return {
      mode: CONSENT_MODES.some(m => m.id === raw.mode) ? raw.mode : 'ask',
      testLocallyBeforeApplying: raw.testLocallyBeforeApplying !== false,
      perDeviceMemory: raw.perDeviceMemory !== false,
      dismissed: raw.dismissed && typeof raw.dismissed === 'object' ? raw.dismissed : {},
    }
  } catch {
    return { ...DEFAULT_CONSENT, dismissed: {} }
  }
}

export function saveConsent(consent) {
  try {
    localStorage.setItem(CONSENT_KEY, JSON.stringify({ ...DEFAULT_CONSENT, ...consent }))
    return true
  } catch {
    return false
  }
}

// Память по устройствам: у гарнитуры и встроенного микрофона разные нужды
export function loadDeviceMemory() {
  try {
    const raw = JSON.parse(localStorage.getItem(DEVICE_KEY) || '{}')
    return raw && typeof raw === 'object' ? raw : {}
  } catch {
    return {}
  }
}

export function rememberDevice(deviceId, settings) {
  if (!deviceId) return loadDeviceMemory()
  const all = loadDeviceMemory()
  all[deviceId] = { settings, at: Date.now() }
  try {
    localStorage.setItem(DEVICE_KEY, JSON.stringify(all))
  } catch {
    /* приватный режим — память живёт до перезагрузки */
  }
  return all
}

export function recallDevice(deviceId) {
  if (!deviceId) return null
  return loadDeviceMemory()[deviceId]?.settings || null
}

export function forgetDevices() {
  try {
    localStorage.removeItem(DEVICE_KEY)
  } catch {
    /* см. выше */
  }
  return {}
}

// ─── Мастер проверки микрофона: четыре реальных шага ────────────────────────

export const WIZARD_STEPS = [
  { id: 'connection', label: 'Check mic connection', ru: 'Микрофон подключён и не заглушён', seconds: 0 },
  { id: 'level', label: 'Check input level', ru: 'Уровень входа в рабочем диапазоне', seconds: 4 },
  { id: 'noise', label: 'Listen for background noise', ru: 'Фоновый шум — помолчи 4 секунды', seconds: 4 },
  { id: 'environment', label: 'Test in your environment', ru: 'Проба голоса: скажи пару фраз', seconds: 5 },
]

// Проверка шага по накопленным метрикам. Возвращает { ok, note }.
export function evaluateWizardStep(stepId, { chain, snapshot }) {
  if (stepId === 'connection') {
    const track = chain?.stream?.getAudioTracks?.()[0]
    if (!track) return { ok: false, note: 'Микрофон не захвачен — разреши доступ в браузере.' }
    if (track.muted) return { ok: false, note: 'Микрофон заглушён системой.' }
    if (track.readyState !== 'live') return { ok: false, note: 'Устройство отключилось.' }
    return { ok: true, note: track.label || 'Микрофон на связи.' }
  }
  if (stepId === 'level') {
    const v = verdictFor(snapshot)
    if (v.id === 'silent') return { ok: false, note: 'Сигнала нет — проверь, тот ли микрофон выбран.' }
    if (v.id === 'good') return { ok: true, note: `Уровень ${snapshot.rmsDb.toFixed(1)} dB — в рабочем диапазоне.` }
    return { ok: false, note: `Уровень ${snapshot.rmsDb.toFixed(1)} dB — ${v.ru.toLowerCase()}.` }
  }
  if (stepId === 'noise') {
    const floor = snapshot?.noiseFloorDb ?? -100
    if (floor <= -55) return { ok: true, note: `Фон ${floor.toFixed(0)} dB — тихо.` }
    if (floor <= -45) return { ok: true, note: `Фон ${floor.toFixed(0)} dB — терпимо, шумодав справится.` }
    return { ok: false, note: `Фон ${floor.toFixed(0)} dB — шумно. Пресет NOISY ROOM или гарнитура.` }
  }
  if (stepId === 'environment') {
    if (!snapshot || snapshot.clippingCount > 2) {
      return { ok: false, note: `Перегруз ${snapshot?.clippingCount || 0} раз — убавь усиление.` }
    }
    return { ok: true, note: 'Проба записана — сравни A/B на экране обработки.' }
  }
  return { ok: false, note: '' }
}
